import { useMemo } from 'react';

import { useAppSelector, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectCohortDefinition,
  selectConceptGroups,
} from '@verantos-dev/app-state-cohort-builder';
import { SubjectVariableCriteriaSelectionTypeLegacy } from '@verantos-dev/types-cohorts';

import { checkFilterForMissingConcept } from '../../utils/concept-group-utils';
import { extractEventName } from '../../utils/event-utils';

export const useCheckCohortSummaryForErrors = () => {
  const restrictSaveProcess = useFeatureFlagSplit('restrict_save_process_on_error');

  const cohortDefinition = useAppSelector(selectCohortDefinition);
  const allConceptsList = useAppSelector(selectConceptGroups);

  const simpleEvents = useMemo(
    () => cohortDefinition?.simpleEvents || [],
    [cohortDefinition?.simpleEvents]
  );

  const derivedEvents = useMemo(
    () => cohortDefinition?.derivedEvents || [],
    [cohortDefinition?.derivedEvents]
  );

  const subjectVariables = useMemo(
    () => cohortDefinition?.subjectVariables || [],
    [cohortDefinition?.subjectVariables]
  );

  const inclusionCriteria = useMemo(
    () =>
      subjectVariables.filter(
        (criterion) =>
          criterion.criteriaSelection === SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION
      ),
    [subjectVariables]
  );

  const exclusionCriteria = useMemo(
    () =>
      subjectVariables.filter(
        (criterion) =>
          criterion.criteriaSelection === SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION
      ),
    [subjectVariables]
  );

  const simpleEventNames = useMemo(
    () => simpleEvents?.map((simpleEvent) => simpleEvent?.name) || [],
    [simpleEvents]
  );
  const derivedEventNames = useMemo(
    () => derivedEvents?.map((derivedEvent) => derivedEvent?.name) || [],
    [derivedEvents]
  );
  const subjectVariableEventNames = useMemo(
    () => subjectVariables?.map((subjectVariable) => subjectVariable?.name) || [],
    [subjectVariables]
  );
  const allEventNames = useMemo(
    () => [...simpleEventNames, ...derivedEventNames, ...subjectVariableEventNames],
    [simpleEventNames, derivedEventNames, subjectVariableEventNames]
  );

  // Returns an array of ids of simple events that have concept filters but don't have any concepts selected
  const simpleEventIdsWithNonExistingConcepts = useMemo(() => {
    const eventIds: string[] = [];

    if (!simpleEvents?.length || !restrictSaveProcess) {
      return eventIds;
    }

    simpleEvents?.forEach((simpleEvent) => {
      simpleEvent?.conceptFilters?.forEach((conceptFilter) => {
        const isMissingConcept = checkFilterForMissingConcept({
          filter: conceptFilter,
          conceptFieldIndex: 2,
          allConceptsList,
        });

        if (isMissingConcept) {
          eventIds.push(simpleEvent?.id);
        }
      });

      simpleEvent?.relatedEntityFilters?.forEach((relatedEntityFilter) => {
        const isMissingConcept = checkFilterForMissingConcept({
          filter: relatedEntityFilter,
          conceptFieldIndex: 3,
          allConceptsList,
        });

        if (isMissingConcept) {
          eventIds.push(simpleEvent?.id);
        }
      });
    });

    return Array.from(new Set(eventIds));
  }, [simpleEvents, restrictSaveProcess, allConceptsList]);

  // Returns an array of ids of derived events that have event filters but don't have any events selected
  const eventIdsWithMissingEventNames = useMemo(() => {
    const eventIds: string[] = [];

    if (!derivedEvents?.length || !restrictSaveProcess) {
      return eventIds;
    }

    derivedEvents?.forEach((derivedEvent) => {
      derivedEvent?.eventFilters?.forEach((eventFilter) => {
        const eventName = extractEventName(eventFilter?.eventName?.value);
        if (!allEventNames.includes(eventName || '')) {
          eventIds.push(derivedEvent.id);
        }
      });
    });

    return Array.from(new Set(eventIds));
  }, [derivedEvents, restrictSaveProcess, allEventNames]);

  // Returns an array of ids of events that have duplicate event names
  const eventIdsWithDuplicateEventNames = useMemo(() => {
    const mapping: Record<string, string> = {};
    const allEvents = [...(simpleEvents || []), ...(derivedEvents || [])];
    const duplicates = new Set<string>();

    // loop through all events to get mapping of event names to event ids
    allEvents.forEach((event) => {
      const name = event?.name;
      if (name && !mapping[name]) {
        mapping[name] = event.id;
      } else if (mapping[name]) {
        duplicates.add(event.id);
        duplicates.add(mapping[name]);
      }
    });

    return Array.from(duplicates);
  }, [simpleEvents, derivedEvents]);

  // Returns an array of ids of inclusion criteria that have count filters but don't have any events selected
  const inclusionCriteriaIdsWithMissingEventNames = useMemo(() => {
    const eventIds: string[] = [];

    if (!inclusionCriteria?.length || !restrictSaveProcess) {
      return eventIds;
    }

    inclusionCriteria.forEach((criterion) => {
      if (!criterion?.countFilter) return;
      const eventName = extractEventName(criterion.countFilter?.inputFields[0]?.value);
      if (!allEventNames.includes(eventName || '')) {
        eventIds.push(criterion.id);
      }
    });

    return Array.from(new Set(eventIds));
  }, [inclusionCriteria, restrictSaveProcess, allEventNames]);

  // Returns an array of ids of exclusion criteria that have count filters but don't have any events selected
  const exclusionCriteriaIdsWithMissingEventNames = useMemo(() => {
    const eventIds: string[] = [];
    if (!exclusionCriteria?.length || !restrictSaveProcess) {
      return eventIds;
    }

    exclusionCriteria.forEach((criterion) => {
      if (!criterion?.countFilter) return;
      const eventName = extractEventName(criterion.countFilter?.inputFields[0]?.value);
      if (!allEventNames.includes(eventName || '')) {
        eventIds.push(criterion.id);
      }
    });

    return Array.from(new Set(eventIds));
  }, [exclusionCriteria, restrictSaveProcess, allEventNames]);

  // Returns an array of ids for inclusion criteria that have duplicate names
  const inclusionCriteriaIdsWithDuplicateNames = useMemo(() => {
    const mapping: Record<string, string> = {};
    const duplicates = new Set<string>();

    (inclusionCriteria || []).forEach((criterion) => {
      const name = criterion?.name;
      if (name) {
        if (!mapping[name]) {
          mapping[name] = criterion.id;
        } else {
          duplicates.add(criterion.id);
          duplicates.add(mapping[name]);
        }
      }
    });

    return Array.from(duplicates);
  }, [inclusionCriteria]);

  // Returns an array of ids for exclusion criteria that have duplicate names
  const exclusionCriteriaIdsWithDuplicateNames = useMemo(() => {
    const mapping: Record<string, string> = {};
    const duplicates = new Set<string>();

    (exclusionCriteria || []).forEach((criterion) => {
      const name = criterion?.name;
      if (name) {
        if (!mapping[name]) {
          mapping[name] = criterion.id;
        } else {
          duplicates.add(criterion.id);
          duplicates.add(mapping[name]);
        }
      }
    });

    return Array.from(duplicates);
  }, [exclusionCriteria]);

  return {
    simpleEventIdsWithNonExistingConcepts,
    eventIdsWithMissingEventNames,
    eventIdsWithDuplicateEventNames,
    inclusionCriteriaIdsWithMissingEventNames,
    inclusionCriteriaIdsWithDuplicateNames,
    exclusionCriteriaIdsWithMissingEventNames,
    exclusionCriteriaIdsWithDuplicateNames,
  };
};
